/**
 * Read and consume the install-attribution cookie set by the marketing site.
 *
 * The marketing site (overlayqa.com) writes an `oqa_ref` cookie on
 * `.overlayqa.com` whenever a user clicks a CTA. Format:
 *   `<source>|<path>|<timestamp>`
 * e.g. `blog-cta|/blog/design-debt/|1717500000000`
 *
 * This module reads it, fires a PostHog event, and deletes the cookie so
 * the same click is never double-counted. Zero load-time cost: only called
 * from the pages that need it (SignInPage).
 */

import { capture } from './analytics';
import { log } from './logger';

const COOKIE_NAME = 'oqa_ref';

export interface Attribution {
  source: string;
  path: string;
  timestamp: number;
}

/** Parse the oqa_ref cookie. Returns null if absent or malformed. */
export function readAttribution(): Attribution | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]+)`));
    if (!match) return null;
    const decoded = decodeURIComponent(match[1]);
    const [source, path, ts] = decoded.split('|');
    if (!source || !path) return null;
    return { source, path, timestamp: Number(ts) || 0 };
  } catch {
    return null;
  }
}

/** Delete the cookie so the same click is never double-counted. */
function clearCookie(): void {
  document.cookie = `${COOKIE_NAME}=; domain=.overlayqa.com; path=/; max-age=0; Secure`;
}

/**
 * Read the attribution cookie, fire an `install_attributed` PostHog event,
 * and delete the cookie. Safe to call multiple times; no-ops if already
 * consumed or absent. Returns the attribution data (or null) so callers
 * can forward it.
 */
export function consumeAttribution(): Attribution | null {
  const attr = readAttribution();
  if (!attr) return null;

  log.info('[Attribution] Found marketing referrer:', { ...attr });
  capture('install_attributed', {
    referrer_source: attr.source,
    referrer_path: attr.path,
    referrer_ts: attr.timestamp,
  });
  clearCookie();
  return attr;
}
